import { Component, DestroyRef, Input, type OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type FormControl } from '@angular/forms';
import { type MatAutocomplete } from '@angular/material/autocomplete';

import { type Country } from '../../../modules/master-data/countries/country.model';
import { CountryService } from '../../../modules/master-data/countries/country.service';
import { defaultDialCountry, dialCode, flagEmoji, splitE164 } from '../../utils/phone-number';

/**
 * A mobile number entered as a country code from a dropdown plus the national number (October 2026,
 * TDD 16). The server stores it as E.164 (`+919845011223`); this splits a stored value back into the two
 * controls, including one patched in after the form loads. Send both: `countryCodeControl` holds the code's
 * digits (`91`), `numberControl` the number as typed.
 *
 * Pass `autocomplete` to attach a `mat-autocomplete` to the number (the contact lookup). The number's
 * `required` and `invalidMobile` (`mobileValidator`) errors are shown under it; `required` only sets the asterisk.
 */
@Component({
  selector: 'app-phone-input',
  standalone: false,
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss'
})
export class PhoneInputComponent implements OnInit {
  @Input({ required: true }) countryCodeControl!: FormControl<string | null>;
  @Input({ required: true }) numberControl!: FormControl<string | null>;
  @Input() label = '';
  @Input() required = false;
  @Input() autocomplete: MatAutocomplete | null = null;

  private countryService = inject(CountryService);
  private destroyRef = inject(DestroyRef);

  countries = signal<Country[]>([]);
  readonly dialCode = dialCode;
  readonly flagEmoji = flagEmoji;

  ngOnInit(): void {
    this.countryService.getCountriesForDropdown().subscribe(all => {
      this.countries.set((all ?? []).filter(c => dialCode(c)));
      this.splitStoredValue();
      if (!this.countryCodeControl.value) {
        this.countryCodeControl.setValue(dialCode(defaultDialCountry(this.countries())));
      }
    });
    // An edit form patches the stored number in after it loads.
    this.numberControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.splitStoredValue());
  }

  /** The country the code control points at, for the closed dropdown's flag. */
  selectedCountry(): Country | undefined {
    return this.countries().find(c => dialCode(c) === this.countryCodeControl.value);
  }

  private splitStoredValue(): void {
    // An attached autocomplete briefly sets the picked option (an object) as the value.
    const value = this.numberControl.value;
    if (typeof value !== 'string' || !value.startsWith('+') || !this.countries().length) return;
    const split = splitE164(value, this.countries());
    if (split.dialCode) {
      this.countryCodeControl.setValue(split.dialCode);
      this.numberControl.setValue(split.national, { emitEvent: false });
    }
  }
}
